import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { ContentReviewResponse, ContentReviewResponseV2, ReviewResultsV2 } from '../../common/general-types/ai-video.dto';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { HasBoltOnDirectiveDirective } from '../../common/directives/has-bolt-on/has-bolt-on-directive.directive';
import { environment } from '../../../environments/environment';
import { NotHasBoltOnDirectiveDirective } from '../../common/directives/not-has-bolt-on/not-has-bolt-on-directive.directive';
import { ChoosePlanService } from '../../pages/choose-plan/service/choose-plan/choose-plan.service';

@Component({
  selector: 'atom-individual-post-ai-review',
  standalone: true,
  imports: [DecimalPipe, NgTemplateOutlet, HasBoltOnDirectiveDirective, NotHasBoltOnDirectiveDirective],
  templateUrl: './individual-post-ai-review.component.html',
  styleUrl: './individual-post-ai-review.component.css'
})
export class IndividualPostAiReviewComponent implements OnInit, OnChanges {

  environment = environment;

  @Input('reviewHistory') reviewHistory: any | ContentReviewResponse | ContentReviewResponseV2 | undefined;

  /** Score and checklist only. Skips bolt-on gating and send-feedback. */
  @Input() resultsOnly = false;

  @Output() sendSuggestedFeedbackEvent = new EventEmitter<string>();

  doCollapsed = signal<boolean>(true);
  dontCollapsed = signal<boolean>(true);
  sendFeedbackCollapsed = signal<boolean>(false);

  doResults = signal<ReviewResultsV2[]>([]);
  dontResults = signal<ReviewResultsV2[]>([]);

  firstFailedResult = signal<ReviewResultsV2 | undefined>(undefined);
  firstDoResult = signal<ReviewResultsV2 | undefined>(undefined);
  firstDontResult = signal<ReviewResultsV2 | undefined>(undefined);

  defaultMessageText = signal<string | undefined>(undefined);

  /** Services */
  public choosePlanService = inject(ChoosePlanService);
  /** Services END */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reviewHistory']) {
      this.applyReviewHistory();
    }
  }

  ngOnInit(): void {
    this.applyReviewHistory();
  }

  private applyReviewHistory(): void {
    if (this.reviewHistory?.reviewResults?.length > 0) {
      this.splitDoAndDont(this.reviewHistory.reviewResults);
      this.setFeedbackMessage();
      return;
    }

    this.doResults.set([]);
    this.dontResults.set([]);
    this.firstFailedResult.set(undefined);
    this.firstDoResult.set(undefined);
    this.firstDontResult.set(undefined);
    this.defaultMessageText.set(undefined);
  }

  splitDoAndDont(reviewResults: ReviewResultsV2[]): void {
    if (reviewResults && reviewResults.length > 0) {

      const firstFailedResult = reviewResults.find((result: ReviewResultsV2) => result.answer !== 'Approved');
      this.firstFailedResult.set(firstFailedResult ?? reviewResults[0]);

      const doResults = reviewResults.filter((result: ReviewResultsV2) => result.type === 'do');
      const sortedDoResults = [...doResults].sort((a: ReviewResultsV2, b: ReviewResultsV2) => {
        const aNonApproved = a.answer !== 'Approved';
        const bNonApproved = b.answer !== 'Approved';
        if (aNonApproved !== bNonApproved) {
          return aNonApproved ? -1 : 1;
        }
        return a.statement.localeCompare(b.statement);
      });
      this.doResults.set(sortedDoResults);

      const firstNonApproved = sortedDoResults.find((result: ReviewResultsV2) => result.answer !== 'Approved');
      this.firstDoResult.set(firstNonApproved ?? sortedDoResults[0] ?? undefined);

      this.dontResults.set(reviewResults.filter((result: ReviewResultsV2) => result.type === 'dont'));
    }
  }

  setFeedbackMessage() {
    if (this.reviewHistory && this.reviewHistory.reviewResults && this.reviewHistory.reviewResults.length > 0) {
      for (let result of this.reviewHistory.reviewResults) {
        if (result.suggestedFix) {
          this.defaultMessageText.set(`Hello, Thank you for your submission 😊. \nHere are some suggested updates for your next draft:\n${this.suggestedFixes().map((fix: string, index: number) => `${index + 1}. ${fix}`).join('\n')}\n\nPlease review the suggested updates and make the necessary changes.\nThank you for your time and effort. `);
        }
      }
    }
  }

  suggestedFixes() {
    const reviewResults = this.reviewHistory?.reviewResults ?? [];
    return reviewResults
      .filter((r: ReviewResultsV2) => r.suggestedFix)
      .map((r: ReviewResultsV2) => r.suggestedFix as string);
  }

  sendSuggestedFeedback() {
    if (this.defaultMessageText()) {
      this.sendSuggestedFeedbackEvent.emit(this.defaultMessageText()!);
    }
  }

  openChoosePlan() {
    this.choosePlanService.launchSubscribeOrBoltOn(environment.stripe.shaas21products.bo_ai_moderation);
  }
}
